import React, { useState, useRef, useCallback, useEffect } from 'react'
import Webcam from 'react-webcam'
import Avatar from './components/Avatar'
import PhotoRealisticAvatar from './components/PhotoRealisticAvatar'
import MeetingRoomReservation from './components/MeetingRoomReservation'
import AdminDashboard from './components/AdminDashboard'
import LoginScreen from './components/LoginScreen'
import AdminPanel from './components/AdminPanel'
import ReceptionLayout from './components/ReceptionLayout'
import AdminReceptionLayout from './components/AdminReceptionLayout'
import SimpleFloorMap from './components/SimpleFloorMap'
import PlanComparison from './components/PlanComparison'
import NavigationHeader from './components/NavigationHeader'
import { PlanVideoTrigger } from './components/AvatarVideoPlayer'
import { alsokTheme, componentThemes, uiTokens, themeHelpers } from './styles/alsok-theme'
import { avatarService } from './services/avatarService'
import { DEFAULT_RECEPTION_STATUS, loadReceptionStatus, saveReceptionStatus } from './utils/receptionStatusDefaults'
import { receptionStatusManager } from './services/receptionStatusManager'
import { useReceptionStatus } from './hooks/useReceptionStatus'
import { usePlanFeatures } from './hooks/usePlanFeatures'
import { getCurrentPlan, PLAN_TYPES } from './config/planConfig'

// Mode and Permission System (プラン対応版)
const MODES = {
  LOGIN: 'login',
  PLAN_DEMO: 'plan-demo',           // プランデモ・比較画面
  ADMIN_DASHBOARD: 'admin-dashboard',
  ADMIN_RECEPTION: 'admin-reception',
  CUSTOMER_RECEPTION: 'customer-reception',
  ROOM_BOOKING: 'room-booking',
}

const ROLE_PERMISSIONS = {
  administrator: [
    MODES.PLAN_DEMO,
    MODES.ADMIN_DASHBOARD,
    MODES.ADMIN_RECEPTION, 
    MODES.CUSTOMER_RECEPTION,
    MODES.ROOM_BOOKING
  ],
  staff: [
    MODES.PLAN_DEMO,
    MODES.ADMIN_DASHBOARD,
    MODES.ADMIN_RECEPTION,
    MODES.CUSTOMER_RECEPTION,
    MODES.ROOM_BOOKING
  ],
  'reception-only': [
    MODES.CUSTOMER_RECEPTION,
    MODES.ROOM_BOOKING
  ]
}

const canAccessMode = (userRole, mode) => {
  return ROLE_PERMISSIONS[userRole]?.includes(mode) || false
}

const getDefaultModeForRole = (userRole) => {
  if (userRole === 'administrator' || userRole === 'staff') {
    return MODES.PLAN_DEMO  // 管理者はプランデモから開始
  }
  return MODES.CUSTOMER_RECEPTION
}

function App() {
  // 基本状態管理
  const [currentMode, setCurrentMode] = useState(MODES.LOGIN)
  const [user, setUser] = useState(null)
  const [isRecording, setIsRecording] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [response, setResponse] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [currentAvatar, setCurrentAvatar] = useState('sato')
  const [showCamera, setShowCamera] = useState(false)
  const [receptionText, setReceptionText] = useState('いらっしゃいませ！')
  const [avatarExpression, setAvatarExpression] = useState('neutral')
  const [guestInfo, setGuestInfo] = useState(null)
  
  // プラン機能Hook
  const { currentPlan, planFeatures, features, refreshPlan } = usePlanFeatures()
  
  // プラン変更処理
  const handlePlanChange = useCallback((newPlanType) => {
    console.log(`🔄 プラン変更: ${currentPlan} → ${newPlanType}`)
    
    // プラン機能を即座に更新
    setTimeout(() => {
      refreshPlan()
    }, 100)
    
    // カスタムイベントでプラン変更を通知
    window.dispatchEvent(new CustomEvent('planChanged', { 
      detail: { 
        oldPlan: currentPlan, 
        newPlan: newPlanType 
      } 
    }))
  }, [currentPlan, refreshPlan])
  
  // 受付状態管理
  const {
    receptionStatus,
    updateStatus,
    resetStatus
  } = useReceptionStatus()

  // Refs
  const webcamRef = useRef(null)
  const recognitionRef = useRef(null)
  const synthRef = useRef(null)

  // ログイン処理
  const handleLogin = useCallback((userData) => {
    setUser(userData)
    const defaultMode = getDefaultModeForRole(userData.role)
    setCurrentMode(defaultMode)
  }, [])

  // ログアウト処理
  const handleLogout = useCallback(() => {
    setUser(null)
    setCurrentMode(MODES.LOGIN)
    resetStatus()
  }, [resetStatus])

  // モード切替処理
  const switchMode = useCallback((mode) => {
    if (user && canAccessMode(user.role, mode)) {
      setCurrentMode(mode)
    }
  }, [user])

  // ホームに戻る処理
  const handleHome = useCallback(() => {
    if (user) {
      const defaultMode = getDefaultModeForRole(user.role)
      setCurrentMode(defaultMode)
    }
  }, [user])

  // 音声認識開始（プラン機能制御）
  const startListening = useCallback(() => {
    if (!features.voiceReception) {
      alert(`${planFeatures.name}では音声受付機能は利用できません`)
      return
    }

    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('お使いのブラウザは音声認識に対応していません')
      return
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    recognitionRef.current = new SpeechRecognition()
    
    recognitionRef.current.continuous = false
    recognitionRef.current.interimResults = false
    recognitionRef.current.lang = 'ja-JP'

    recognitionRef.current.onstart = () => {
      setIsRecording(true)
      setTranscript('')
    }

    recognitionRef.current.onresult = (event) => {
      const transcript = event.results[0][0].transcript
      setTranscript(transcript)
      handleSpeechInput(transcript)
    }

    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error)
      setIsRecording(false)
    }

    recognitionRef.current.onend = () => {
      setIsRecording(false)
    }

    recognitionRef.current.start()
  }, [features.voiceReception, planFeatures.name])

  // 音声入力処理（プラン別対応）
  const handleSpeechInput = useCallback(async (inputText) => {
    setIsProcessing(true)
    
    try {
      let responseText = ''
      
      // プラン別の応答処理
      if (features.aiConversation) {
        // 松プラン: AI対話機能
        responseText = await handleAIConversation(inputText)
      } else if (features.visualNavigation) {
        // 竹プラン: 視覚ナビ対応
        responseText = handleVisualNavigation(inputText)
      } else {
        // 梅プラン: 基本応答
        responseText = handleBasicReception(inputText)
      }
      
      setResponse(responseText)
      
      // 音声合成で応答
      if (features.voiceReception && synthRef.current) {
        const utterance = new SpeechSynthesisUtterance(responseText)
        utterance.lang = 'ja-JP'
        utterance.rate = 0.9
        speechSynthesis.speak(utterance)
      }
      
      // アバター表情更新（竹・松プラン）
      if (features.avatarMotion) {
        setAvatarExpression('speaking')
        setTimeout(() => setAvatarExpression('neutral'), 3000)
      }
      
    } catch (error) {
      console.error('Speech processing error:', error)
      setResponse('申し訳ございません。もう一度お話しください。')
    } finally {
      setIsProcessing(false)
    }
  }, [features])

  // AI対話処理（松プラン専用）
  const handleAIConversation = async (inputText) => {
    // TODO: ChatGPT API連携
    return `AI対話機能での応答: ${inputText}について詳しくご案内いたします。`
  }

  // 視覚ナビ処理（竹プラン）
  const handleVisualNavigation = (inputText) => {
    if (inputText.includes('場所') || inputText.includes('案内')) {
      return '地図でご案内いたします。画面の矢印に従ってお進みください。'
    }
    return 'ご来社ありがとうございます。何かご案内できることはございますか？'
  }

  // 基本受付処理（梅プラン）
  const handleBasicReception = (inputText) => {
    if (inputText.includes('受付')) {
      return 'こんにちは。受付はこちらです。お待ちください。'
    }
    return 'いらっしゃいませ。担当者にお取り次ぎいたします。'
  }

  // 顔認証処理（松プラン専用）
  const handleFaceAuth = useCallback(async () => {
    if (!features.faceAuth) {
      alert(`${planFeatures.name}では顔認証機能は利用できません`)
      return
    }
    
    // 顔認証ロジック
    // TODO: Azure Face API連携
    setGuestInfo({ name: '田中様', status: 'recognized' })
  }, [features.faceAuth, planFeatures.name])

  // キーボードショートカット
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.shiftKey) {
        switch (event.key) {
          case 'P':
            event.preventDefault()
            if (user && (user.role === 'administrator' || user.role === 'staff')) {
              switchMode(MODES.PLAN_DEMO)
            }
            break
          case 'A':
            event.preventDefault()
            if (user && (user.role === 'administrator' || user.role === 'staff')) {
              switchMode(MODES.ADMIN_DASHBOARD)
            }
            break
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [user, switchMode])

  // プラン別レンダリング
  const renderCurrentMode = () => {
    switch (currentMode) {
      case MODES.LOGIN:
        return (
          <LoginScreen 
            onLogin={handleLogin}
            theme={alsokTheme}
          />
        )

      case MODES.PLAN_DEMO:
        return (
          <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
            <NavigationHeader
              user={user}
              currentMode={currentMode}
              onHome={handleHome}
              onLogout={handleLogout}
              title="プランデモ"
              showHome={false} // プランデモがホームなので非表示
            />
            
            <PlanComparison 
              showSwitcher={true} 
              onPlanChange={handlePlanChange}
            />
            
            <div className="flex-responsive spacing-responsive" style={{ textAlign: 'center' }}>
              <button
                className="btn-responsive"
                onClick={() => switchMode(MODES.CUSTOMER_RECEPTION)}
                style={{
                  backgroundColor: planFeatures.color,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                🎬 {planFeatures.name}デモを開始
              </button>
              
              {user && (user.role === 'administrator' || user.role === 'staff') && (
                <button
                  className="btn-responsive"
                  onClick={() => switchMode(MODES.ADMIN_DASHBOARD)}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  🔧 管理画面へ
                </button>
              )}
            </div>
          </div>
        )

      case MODES.CUSTOMER_RECEPTION:
        return (
          <PlanVideoTrigger 
            trigger="mount" 
            onComplete={() => console.log(`${planFeatures.name} intro completed`)}
          >
            <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
              <NavigationHeader
                user={user}
                currentMode={currentMode}
                onHome={handleHome}
                onLogout={handleLogout}
                title="AI受付"
              />
              <ReceptionLayout
                isRecording={isRecording}
                transcript={transcript}
                response={response}
                isProcessing={isProcessing}
                onStartListening={startListening}
                onLogout={handleLogout}
                currentAvatar={currentAvatar}
                onAvatarChange={features.avatarCustomization ? setCurrentAvatar : null}
                guestInfo={guestInfo}
                onFaceAuth={features.faceAuth ? handleFaceAuth : null}
                showMap={features.mapDisplay}
                planFeatures={planFeatures}
              />
            </div>
          </PlanVideoTrigger>
        )

      case MODES.ADMIN_DASHBOARD:
        return (
          <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
            <NavigationHeader
              user={user}
              currentMode={currentMode}
              onHome={handleHome}
              onLogout={handleLogout}
              title="管理ダッシュボード"
            />
            <AdminDashboard
              user={user}
              onLogout={handleLogout}
              onModeSwitch={switchMode}
              canAccessModes={Object.values(MODES).filter(mode => canAccessMode(user?.role, mode))}
              planFeatures={planFeatures}
            />
          </div>
        )

      case MODES.ADMIN_RECEPTION:
        return (
          <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
            <NavigationHeader
              user={user}
              currentMode={currentMode}
              onHome={handleHome}
              onLogout={handleLogout}
              title="管理受付"
            />
            <AdminReceptionLayout
              user={user}
              onLogout={handleLogout}
              onModeSwitch={switchMode}
              planFeatures={planFeatures}
            />
          </div>
        )

      case MODES.ROOM_BOOKING:
        return (
          <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
            <NavigationHeader
              user={user}
              currentMode={currentMode}
              onHome={handleHome}
              onLogout={handleLogout}
              title="会議室予約"
            />
            <MeetingRoomReservation
              user={user}
              onBack={() => switchMode(getDefaultModeForRole(user?.role))}
              planFeatures={planFeatures}
            />
          </div>
        )

      default:
        return <div>Unknown mode: {currentMode}</div>
    }
  }

  return (
    <div className="App" style={{ minHeight: '100vh' }}>
      {renderCurrentMode()}
      
      {/* プラン情報表示（開発用） */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'fixed',
          bottom: '10px',
          right: '10px',
          backgroundColor: planFeatures.color,
          color: 'white',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '12px',
          zIndex: 1000
        }}>
          {planFeatures.name} | {currentMode}
        </div>
      )}
    </div>
  )
}

export default App